import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, Download, ChatCircle, Clock, ArrowLeft, LockKey, LockKeyOpen, Info, Buildings, CalendarBlank, Tag, User, Folder, FilePdf, File } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { sortByRevision, getLatestRevision } from '../utils/revisionSort';
const API = `${BACKEND_URL}/api`;

const DocumentDetail = ({ user }) => {
  const { id } = useParams();
  const [currentDoc, setCurrentDoc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [latestRevision, setLatestRevision] = useState(null);

  useEffect(() => {
    fetchDocument();
    fetchVersions();
    fetchComments();
    fetchLatestRevision();
  }, [id]);

  const fetchDocument = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}`);
      setCurrentDoc(response.data);
    } catch (error) {
      toast.error('Failed to fetch document');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}/versions`);
      setVersions(response.data);
    } catch (error) {
      console.error('Failed to fetch versions', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments', error);
    }
  };

  const fetchLatestRevision = async () => {
    try {
      const response = await axios.get(`${API}/cycles?document_id=${id}`);
      if (response.data && response.data.length > 0) {
        // Get all revisions for this document
        const docRevisions = response.data.filter(r => r.document_id === id);
        if (docRevisions.length > 0) {
          // Sort by created_at descending to get the latest first
          const sorted = sortByRevision(docRevisions, 'revision', false);
          setLatestRevision(sorted[sorted.length - 1]);
          setRevisions(sorted);
        }
      }
    } catch (error) {
      console.error('Failed to fetch revisions', error);
    }
  };

  const [revisions, setRevisions] = useState([]);

  const handleDownloadPdf = async () => {
    if (!latestRevision?.pdf_file_name) {
      toast.error('No PDF file available');
      return;
    }
    try {
      const response = await axios.get(`${API}/cycles/${latestRevision.id}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', latestRevision.pdf_file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF download started');
    } catch (error) {
      toast.error('PDF download failed');
    }
  };

  const handleDownloadNative = async () => {
    if (!latestRevision?.native_file_name) {
      toast.error('No native file available');
      return;
    }
    try {
      const response = await axios.get(`${API}/cycles/${latestRevision.id}/download-native`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', latestRevision.native_file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Native file download started');
    } catch (error) {
      toast.error('Native file download failed');
    }
  };

  const handleDownloadOriginal = async () => {
    try {
      const response = await axios.get(`${API}/documents/${id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', currentDoc.name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleCheckout = async () => {
    try {
      await axios.post(`${API}/documents/${id}/checkout`);
      toast.success('Document checked out');
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    }
  };

  const handleCheckin = async () => {
    try {
      await axios.post(`${API}/documents/${id}/checkin`);
      toast.success('Document checked in');
      fetchDocument();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkin failed');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await axios.post(`${API}/documents/${id}/comments`, { content: newComment });
      toast.success('Comment added');
      setNewComment('');
      fetchComments();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z').toLocaleString();
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return null;
    return new Date(String(dateString).endsWith('Z') || String(dateString).includes('+') ? dateString : dateString + 'Z').toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    const b = Number(bytes);
    if (!b || b <= 0) return '—';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    if (b < 1024 * 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB';
    return (b / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  // Component for displaying a field only if it has a value
  const Field = ({ label, value }) => {
    if (!value) return null;
    return (
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
        <p className="font-medium text-sm">{value}</p>
      </div>
    );
  };

  // Component for section with conditional rendering
  const Section = ({ title, icon: Icon, children }) => {
    // Filter out null children
    const validChildren = React.Children.toArray(children).filter(child => child !== null);
    if (validChildren.length === 0) return null;
    
    return (
      <div className="border border-border rounded-sm p-5 bg-white">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-primary uppercase tracking-wide">
          {Icon && <Icon size={18} />}
          {title}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {validChildren}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!document) {
    return <div className="p-6">Document not found</div>;
  }

  // Check if sections have content
  const hasIdentification = currentDoc.document_number || currentDoc.revision || currentDoc.revision_date || currentDoc.company_document_nr || currentDoc.external_refs;
  const hasClassification = currentDoc.discipline || currentDoc.discipline_code || currentDoc.document_type || currentDoc.subclass_code || currentDoc.classification || currentDoc.language;
  const hasProject = currentDoc.project_code || currentDoc.project_title || currentDoc.work_package_code || currentDoc.purchase_order || currentDoc.asset_item;
  const hasPeople = currentDoc.originator || currentDoc.responsible_engineer || currentDoc.company_code;
  const hasSchedule = currentDoc.planned_start || currentDoc.forecast_start || currentDoc.actual_start || currentDoc.planned_end || currentDoc.forecast_end;
  const hasStatus = currentDoc.approval_status || currentDoc.priority || currentDoc.system_main_division || currentDoc.current_review_status;

  return (
    <div className="p-6" data-testid="document-detail-page">
      {/* Header */}
      <div className="mb-6">
        <Link to="/documents" className="inline-flex items-center gap-2 text-primary hover:underline mb-4" data-testid="back-to-documents">
          <ArrowLeft size={20} />
          Back to Documents
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {currentDoc.name}
            </h1>
            {currentDoc.description && (
              <p className="text-muted-foreground">{currentDoc.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            {currentDoc.status === 'available' ? (
              <Button onClick={handleCheckout} variant="outline" className="rounded-sm" data-testid="checkout-document">
                <LockKey size={18} className="mr-2" />
                Check Out
              </Button>
            ) : (
              <Button onClick={handleCheckin} variant="outline" className="rounded-sm" data-testid="checkin-document">
                <LockKeyOpen size={18} className="mr-2" />
                Check In
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Latest Revision Downloads */}
      {latestRevision && (latestRevision.pdf_file_name || latestRevision.native_file_name) && (
        <div className="mb-6 p-4 bg-white border border-border rounded-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wide mb-1">
                Latest Revision: {latestRevision.revision}
              </h3>
              <p className="text-xs text-muted-foreground">
                Updated {formatDate(latestRevision.updated_at || latestRevision.created_at)}
              </p>
            </div>
            <div className="flex gap-3">
              {/* PDF Download */}
              <div className="flex items-center gap-2">
                {latestRevision.pdf_file_name ? (
                  <Button 
                    onClick={handleDownloadPdf} 
                    className="bg-red-600 hover:bg-red-700 rounded-sm"
                    data-testid="download-pdf"
                  >
                    <FilePdf size={18} weight="bold" className="mr-2" />
                    PDF
                  </Button>
                ) : (
                  <Button disabled variant="outline" className="rounded-sm opacity-50">
                    <FilePdf size={18} className="mr-2" />
                    No PDF
                  </Button>
                )}
              </div>
              
              {/* Native Download */}
              <div className="flex items-center gap-2">
                {latestRevision.native_file_name ? (
                  <Button 
                    onClick={handleDownloadNative} 
                    className="bg-blue-600 hover:bg-blue-700 rounded-sm"
                    data-testid="download-native"
                  >
                    <File size={18} weight="bold" className="mr-2" />
                    Native
                  </Button>
                ) : (
                  <Button disabled variant="outline" className="rounded-sm opacity-50">
                    <File size={18} className="mr-2" />
                    No Native
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* File names */}
          <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-4">
            {latestRevision.pdf_file_name && (
              <div className="flex items-center gap-2 text-sm">
                <FilePdf size={16} className="text-red-500" />
                <span className="text-muted-foreground truncate" title={latestRevision.pdf_file_name}>
                  {latestRevision.pdf_file_name}
                </span>
              </div>
            )}
            {latestRevision.native_file_name && (
              <div className="flex items-center gap-2 text-sm">
                <File size={16} className="text-blue-500" />
                <span className="text-muted-foreground truncate" title={latestRevision.native_file_name}>
                  {latestRevision.native_file_name}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Original File Download (if no revision exists) */}
      {!latestRevision && (
        <div className="mb-6 p-4 bg-white border border-border rounded-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wide mb-1">
                Original Document
              </h3>
              <p className="text-xs text-muted-foreground">No revisions created yet</p>
            </div>
            <Button onClick={handleDownloadOriginal} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="download-original">
              <Download size={18} weight="bold" className="mr-2" />
              Download Original
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">

          {/* Document Identification */}
          {hasIdentification && (
            <Section title="Document Identification" icon={Info}>
              <Field label="Document Nr." value={currentDoc.document_number} />
              <Field label="Revision" value={currentDoc.revision} />
              <Field label="Revision Date" value={formatDateOnly(currentDoc.revision_date)} />
              <Field label="Company Document Nr." value={currentDoc.company_document_nr} />
              <Field label="External Refs." value={currentDoc.external_refs} />
            </Section>
          )}

          {/* Classification */}
          {hasClassification && (
            <Section title="Classification" icon={Folder}>
              <Field label="Discipline" value={currentDoc.discipline} />
              <Field label="Discipline Code" value={currentDoc.discipline_code} />
              <Field label="Document Type" value={currentDoc.document_type} />
              <Field label="Subclass Code" value={currentDoc.subclass_code} />
              <Field label="Classification" value={currentDoc.classification} />
              <Field label="Language" value={currentDoc.language} />
            </Section>
          )}

          {/* Project Information */}
          {hasProject && (
            <Section title="Project Information" icon={Buildings}>
              <Field label="Project Code" value={currentDoc.project_code} />
              <Field label="Project Title" value={currentDoc.project_title} />
              <Field label="Work Package Code" value={currentDoc.work_package_code} />
              <Field label="Purchase Order" value={currentDoc.purchase_order} />
              <Field label="Asset Item" value={currentDoc.asset_item} />
            </Section>
          )}

          {/* People & Company */}
          {hasPeople && (
            <Section title="People & Company" icon={User}>
              <Field label="Originator" value={currentDoc.originator} />
              <Field label="Responsible Engineer" value={currentDoc.responsible_engineer} />
              <Field label="Company Code" value={currentDoc.company_code} />
            </Section>
          )}

          {/* Status & Priority */}
          {hasStatus && (
            <Section title="Status & Priority" icon={Tag}>
              <Field label="Approval Status" value={currentDoc.approval_status} />
              <Field label="Priority" value={currentDoc.priority} />
              <Field label="System/Division" value={currentDoc.system_main_division} />
              <Field label="Review Status" value={currentDoc.current_review_status} />
            </Section>
          )}

          {/* Schedule */}
          {hasSchedule && (
            <Section title="Schedule" icon={CalendarBlank}>
              <Field label="Planned Start" value={formatDateOnly(currentDoc.planned_start)} />
              <Field label="Forecast Start" value={formatDateOnly(currentDoc.forecast_start)} />
              <Field label="Actual Start" value={formatDateOnly(currentDoc.actual_start)} />
              <Field label="Planned End" value={formatDateOnly(currentDoc.planned_end)} />
              <Field label="Forecast End" value={formatDateOnly(currentDoc.forecast_end)} />
            </Section>
          )}

          {/* Tags */}
          {currentDoc.tags && currentDoc.tags.length > 0 && (
            <div className="border border-border rounded-sm p-5 bg-white">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-primary uppercase tracking-wide">
                <Tag size={18} />
                Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {currentDoc.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1.5 bg-secondary text-sm rounded-sm" data-testid={`tag-${idx}`}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="border border-border rounded-sm bg-white" data-testid="comments-section">
            <div className="border-b border-border p-4">
              <h3 className="text-sm font-semibold flex items-center gap-2 text-primary uppercase tracking-wide">
                <ChatCircle size={18} />
                Comments ({comments.length})
              </h3>
            </div>
            <div className="p-4">
              {/* Add comment form */}
              <form onSubmit={handleAddComment} className="mb-6" data-testid="add-comment-form">
                <Textarea
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="mb-2 rounded-sm"
                  data-testid="comment-input"
                />
                <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="submit-comment">
                  Add Comment
                </Button>
              </form>

              {/* Comments list */}
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div key={comment.id} className="border-b border-border pb-4 last:border-0" data-testid={`comment-${comment.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center text-white text-sm font-semibold">
                        {comment.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{comment.user_name}</span>
                          <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                        </div>
                        <p className="text-sm">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No comments yet</p>
                )}
              </div>
            </div>
          </div>

          {/* Document Status - At the bottom */}
          <div className="border border-border rounded-sm p-5 bg-white" data-testid="document-status">
            <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-primary uppercase tracking-wide">
              <FileText size={18} />
              Document Status
            </h3>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-3 h-3 rounded-full ${currentDoc.status === 'available' ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <span className="font-medium capitalize">{currentDoc.status}</span>
              {currentDoc.checked_out_by && (
                <span className="text-sm text-muted-foreground ml-2">
                  (Checked out)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Revision History */}
          <div className="border border-border rounded-sm bg-white" data-testid="revision-history">
            <div className="border-b border-border p-4">
              <h3 className="text-sm font-semibold flex items-center gap-2 text-primary uppercase tracking-wide">
                <Clock size={18} />
                Revision History
              </h3>
            </div>
            <div className="p-4">
              {revisions.length > 0 ? (
                <div className="space-y-3">
                  {revisions.map((rev) => (
                    <div key={rev.id} className="p-3 border border-border rounded-sm" data-testid={`revision-${rev.revision}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-primary">Revision {rev.revision}</span>
                        <span className={`text-xs px-2 py-1 rounded-sm ${
                          rev.review_status === 'approved' ? 'bg-green-100 text-green-800' :
                          rev.review_status === 'rejected' ? 'bg-red-100 text-red-800' :
                          rev.review_status === 'in_review' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {rev.review_status?.replace('_', ' ')}
                        </span>
                      </div>
                      
                      {/* Dates */}
                      <div className="space-y-1 text-xs text-muted-foreground mb-2">
                        <div className="flex justify-between">
                          <span>Created:</span>
                          <span>{formatDate(rev.created_at)}</span>
                        </div>
                        {rev.updated_at && rev.updated_at !== rev.created_at && (
                          <div className="flex justify-between">
                            <span>Modified:</span>
                            <span>{formatDate(rev.updated_at)}</span>
                          </div>
                        )}
                      </div>
                      
                      {rev.review_stage && (
                        <div className="text-xs text-muted-foreground mb-2">
                          Stage: <span className="font-medium">{rev.review_stage}</span>
                        </div>
                      )}
                      
                      {/* v27: revision transmittal display - show transmittal nrs per revision */}
                      {(() => {
                        const outForRev = (transmittalHistory.outgoing || []).filter(t => (t.revision || '') === (rev.revision || ''));
                        const inForRev = (transmittalHistory.incoming || []).filter(t => (t.revision || '') === (rev.revision || ''));
                        if (outForRev.length === 0 && inForRev.length === 0 && !rev.outgoing_transmittal_nr && !rev.incoming_transmittal_nr) return null;
                        return (
                          <div className="mb-2 space-y-1 text-xs">
                            {inForRev.map((t, i) => (
                              <div key={`in-${i}`} className="flex items-center gap-1 text-blue-700">
                                <span className="font-medium">Incoming:</span>
                                <span className="font-mono">{t.transmittal_nr}</span>
                                {t.date && <span className="text-muted-foreground">({formatDate(t.date)})</span>}
                              </div>
                            ))}
                            {outForRev.map((t, i) => (
                              <div key={`out-${i}`} className="flex items-center gap-1 text-green-700">
                                <span className="font-medium">Outgoing:</span>
                                <span className="font-mono">{t.transmittal_nr}</span>
                                {t.date && <span className="text-muted-foreground">({formatDate(t.date)})</span>}
                              </div>
                            ))}
                            {inForRev.length === 0 && rev.incoming_transmittal_nr && (
                              <div className="flex items-center gap-1 text-blue-700">
                                <span className="font-medium">Incoming:</span>
                                <span className="font-mono">{rev.incoming_transmittal_nr}</span>
                              </div>
                            )}
                            {outForRev.length === 0 && rev.outgoing_transmittal_nr && (
                              <div className="flex items-center gap-1 text-green-700">
                                <span className="font-medium">Outgoing:</span>
                                <span className="font-mono">{rev.outgoing_transmittal_nr}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Files attached (with per-file size) */}
                      <div className="pt-2 border-t border-border flex flex-wrap gap-3 text-xs">
                        {rev.pdf_file_name && (
                          <span className="flex items-center gap-1 text-red-600" data-testid={`rev-pdf-size-${rev.revision}`}>
                            <FilePdf size={12} /> PDF · {formatFileSize(rev.pdf_file_size)}
                          </span>
                        )}
                        {rev.native_file_name && (
                          <span className="flex items-center gap-1 text-blue-600" data-testid={`rev-native-size-${rev.revision}`}>
                            <File size={12} /> Native · {formatFileSize(rev.native_file_size)}
                          </span>
                        )}
                        {!rev.pdf_file_name && !rev.native_file_name && (
                          <span className="text-muted-foreground">No files</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No revisions yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;
